import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Calendar,
  Clock,
  Download,
  Globe,
  Newspaper,
  TrendingUp,
  User,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import {
  ContactInfo,
  ContactUsService,
} from "@/services/landing/contactUsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";
import {
  fetchImpactStats,
  ImpactStat,
} from "@/services/landing/impactStatsService";
import { subscribeToNewsletter } from "@/services/landing/newsletterService";
import { fetchUpdates, UpdateItem } from "@/services/updatesService";

export default function Updates() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [stats, setStats] = useState<ImpactStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [itemsToShow, setItemsToShow] = useState(3);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterName, setNewsletterName] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState<null | {
    text: string;
    type: "error" | "success";
  }>(null);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  const filterOptions = ["All", "news", "offers", "app"];

  useEffect(() => {
    const loadUpdates = async () => {
      try {
        setLoading(true);
        const [updatesData, contact] = await Promise.all([
          fetchUpdates(),
          ContactUsService.get(),
        ]);
        setUpdates(updatesData);
        setContactInfo(contact);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUpdates();
  }, []);

  // Fetch hero background
  useEffect(() => {
    fetchHeroBackgrounds("updates").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  // Fetch impact stats
  useEffect(() => {
    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const statsData = await fetchImpactStats();
        setStats(statsData);
      } catch (error) {
        console.error("Failed to load stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, []);

  // Reset items to show when filter changes
  useEffect(() => {
    setItemsToShow(3);
  }, [selectedFilter]);

  // Filter updates based on selected category
  const filteredUpdates =
    selectedFilter === "All"
      ? updates
      : updates.filter(
          (item) => item.type.toLowerCase() === selectedFilter.toLowerCase(),
        );

  // Featured update is the first one from filtered results
  const featuredUpdate = filteredUpdates.length > 0 ? filteredUpdates[0] : null;
  // Regular updates (excluding the featured one)
  const regularUpdates = filteredUpdates.slice(1);
  // Paginated updates
  const paginatedUpdates = regularUpdates.slice(0, itemsToShow);
  // Check if there are more items to load
  const hasMore = regularUpdates.length > itemsToShow;

  const loadMore = () => {
    setItemsToShow((prev) => prev + 3);
  };

  // Download weekly updates as PDF
  const downloadWeeklyUpdatesPDF = () => {
    const doc = new jsPDF();

    // Header with Hareeng branding
    doc.setFontSize(26);
    doc.setTextColor(33, 150, 243); // Primary blue color
    doc.text("Hareeng", 105, 20, { align: "center" });

    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text("Weekly Updates Report", 105, 30, { align: "center" });

    // Date range
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    doc.text(
      `Period: ${weekAgo.toLocaleDateString()} - ${today.toLocaleDateString()}`,
      105,
      38,
      { align: "center" },
    );

    // Filter weekly updates
    const weeklyUpdates = updates.filter((update) => {
      const updateDate = new Date(update.created_at || update.updated_at);
      return updateDate >= weekAgo && updateDate <= today;
    });

    if (weeklyUpdates.length === 0) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("No updates available for this week.", 105, 60, {
        align: "center",
      });
    } else {
      // Summary stats
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Updates: ${weeklyUpdates.length}`, 14, 50);

      const newsCount = weeklyUpdates.filter((u) => u.type === "news").length;
      const offersCount = weeklyUpdates.filter(
        (u) => u.type === "offers",
      ).length;
      const appCount = weeklyUpdates.filter((u) => u.type === "app").length;

      doc.text(
        `News: ${newsCount} | Offers: ${offersCount} | App Updates: ${appCount}`,
        14,
        56,
      );

      // Create table data
      const tableData = weeklyUpdates.map((update) => [
        new Date(update.date).toLocaleDateString(),
        update.type.toUpperCase(),
        update.title,
        update.description, // Full description without truncation
      ]);

      // Add table
      autoTable(doc, {
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        body: tableData,
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 22 },
          2: { cellWidth: 50 },
          3: { cellWidth: 90 },
        },
        head: [["Date", "Category", "Title", "Description"]],
        headStyles: {
          fillColor: [33, 150, 243],
          fontStyle: "bold",
          textColor: 255,
        },
        margin: { top: 65 },
        startY: 65,
        styles: {
          cellPadding: 3,
          fontSize: 9,
          overflow: "linebreak",
        },
      });
    }

    // Footer
    const pageCount = (doc as any).internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on ${today.toLocaleDateString()} | © ${today.getFullYear()} Hareeng - Transforming Mobility Across Africa`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" },
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 10,
      );
    }

    // Save the PDF
    const filename = `hareeng-weekly-updates-${today.toISOString().split("T")[0]}.pdf`;
    doc.save(filename);
  };

  // Download all updates as PDF (Press Kit)
  const downloadPressKitPDF = () => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(28);
    doc.setTextColor(33, 150, 243);
    doc.text("Hareeng", 105, 25, { align: "center" });

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("Press Kit & Updates", 105, 35, { align: "center" });

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const today = new Date();
    doc.text(`Generated: ${today.toLocaleDateString()}`, 105, 42, {
      align: "center",
    });

    // Company overview
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("About Hareeng", 14, 55);

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const aboutText =
      "Hareeng is transforming mobility across Africa by providing innovative ride-hailing, food delivery, and scooter services. We connect millions of users with drivers, restaurants, and opportunities.";
    const splitAbout = doc.splitTextToSize(aboutText, 180);
    doc.text(splitAbout, 14, 62);

    // Stats
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Key Statistics", 14, 80);

    doc.setFontSize(10);
    doc.text("• 5M+ Active Users", 14, 88);
    doc.text("• 20 Cities Served", 14, 94);
    doc.text("• 150% Growth in 2024", 14, 100);
    doc.text("• 15 Industry Awards", 14, 106);

    // Recent updates table
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Recent Updates", 14, 120);

    const recentUpdates = updates.slice(0, 10); // Last 10 updates
    const tableData = recentUpdates.map((update) => [
      new Date(update.date).toLocaleDateString(),
      update.type.toUpperCase(),
      update.title,
    ]);

    autoTable(doc, {
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      body: tableData,
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 30 },
        2: { cellWidth: 125 },
      },
      head: [["Date", "Category", "Title"]],
      headStyles: {
        fillColor: [33, 150, 243],
        fontStyle: "bold",
        textColor: 255,
      },
      startY: 125,
      styles: {
        cellPadding: 3,
        fontSize: 9,
      },
    });

    // Contact information (replace the static contact section)
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Media Contact", 14, finalY);

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);

    if (contactInfo) {
      doc.text(`Email: ${contactInfo.email}`, 14, finalY + 8);
      doc.text(`Phone: ${contactInfo.phone}`, 14, finalY + 14);
      doc.text(`Address: ${contactInfo.address}`, 14, finalY + 20);
      if (contactInfo.city && contactInfo.city !== "none") {
        doc.text(`City: ${contactInfo.city}`, 14, finalY + 26);
      }
    } else {
      doc.text("Email: contact@hareeng.org", 14, finalY + 8);
      doc.text("Phone: +266 5048 0814", 14, finalY + 14);
      doc.text("Website: www.hareeng.org", 14, finalY + 20);
    }

    // Footer
    const pageCount = (doc as any).internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `© ${today.getFullYear()} Harehloekiseng - Transforming Cleaning Across Africa`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" },
      );
    }

    // Save
    doc.save(`harehloekiseng-press-kit-${today.toISOString().split("T")[0]}.pdf`);
  };

  // Map icon names to components
  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      HiAward: <Award className="h-full w-full" />,
      HiBadgeCheck: <BadgeCheck className="h-full w-full" />,
      HiGlobe: <Globe className="h-full w-full" />,
      HiLightningBolt: <Zap className="h-full w-full" />,
      HiTrendingUp: <TrendingUp className="h-full w-full" />,
      HiUsers: <Users className="h-full w-full" />,
    };
    return iconMap[iconName] || <Users className="h-full w-full" />;
  };

  // Map color themes to Tailwind classes
  const getColorClasses = (colorTheme: string): string => {
    const colorMap: Record<string, string> = {
      accent: "text-accent",
      error: "text-error",
      info: "text-info",
      primary: "text-primary",
      secondary: "text-secondary",
      success: "text-success",
      warning: "text-warning",
    };
    return colorMap[colorTheme] || "text-primary";
  };

  // Fallback stats if API fails
  const displayStats = stats.length > 0 ? stats : [];

  // Add this function in the component
  const scrollToNewsletter = () => {
    const newsletterSection = document.getElementById("newsletter-section");
    if (newsletterSection) {
      newsletterSection.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Add newsletter subscription handler
  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterName) {
      setNewsletterMessage({
        text: "Please fill in all fields",
        type: "error",
      });
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage(null);

    try {
      const response = await subscribeToNewsletter({
        email: newsletterEmail,
        name: newsletterName,
      });

      if (response.success) {
        setNewsletterMessage({
          text:
            response.message || "Successfully subscribed to our newsletter!",
          type: "success",
        });
        setNewsletterEmail("");
        setNewsletterName("");
      }
    } catch (error: any) {
      setNewsletterMessage({
        text:
          error.response?.data?.message ||
          "Failed to subscribe. Please try again.",
        type: "error",
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style-updates")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style-updates";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <>
      {/* Hero Background Wrapper - only covers navbar + hero section */}
      <div className="fixed top-0 left-0 right-0 z-0 pointer-events-none w-full" style={{ height: "600px" }}>
        <div className="absolute inset-0 opacity-30">
          {heroLoading ? (
            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-info/10 to-primary/10">
              <div
                className="animate-shimmer absolute inset-0 bg-gradient-to-r from-info/10 via-primary/10 to-info/10"
                style={{ backgroundSize: "200% 100%" }}
              />
            </div>
          ) : heroBackground?.imageUrl ? (
            <img
              alt={heroBackground?.imageAlt || "Hero background"}
              className="h-full w-full object-cover"
              src={heroBackground.imageUrl}
              style={{ display: heroBackground?.imageUrl ? "block" : "none" }}
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-info/5 to-primary/5" />
          )}
        </div>
      </div>

      <AppNavbar transparent />

      <div className="relative pt-16">
        <Breadcrumbs transparent />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-transparent z-10">
          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-info-content/20 px-4 py-2">
                <Newspaper size={20} />
                <span className="font-medium">Latest News & Updates</span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Stay updated with{" "}
                <span className="text-primary">Harehloekiseng's journey</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                Get the latest news, company updates, product launches, and
                insights from the Harehloekiseng team as we transform cleaning across
                Africa.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={scrollToNewsletter}
                >
                  Subscribe to Updates
                </button>
                <button
                  className="btn btn-outline btn-lg"
                  onClick={downloadWeeklyUpdatesPDF}
                >
                  <Download size={20} />
                  Download Weekly Updates
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid gap-8 md:grid-cols-4">
              {statsLoading ? (
                <div className="col-span-full flex justify-center py-12">
                  <span className="loading loading-spinner loading-lg"></span>
                </div>
              ) : displayStats.length > 0 ? (
                displayStats.map((stat) => (
                  <div className="text-center" key={stat.id}>
                    <div
                      className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200 ${getColorClasses(stat.color_theme)}`}
                    >
                      {getIconComponent(stat.icon)}
                    </div>
                    <div className="mb-2 text-3xl font-bold">{stat.value}</div>
                    <div className="mb-1 font-semibold text-base-content">
                      {stat.title}
                    </div>
                    <div className="text-sm text-base-content/70">
                      {stat.description}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center text-base-content/70">
                  No stats available at the moment.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Featured News */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12">
              <h2 className="mb-4 text-4xl font-bold">Featured Story</h2>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : featuredUpdate ? (
              <div className="card bg-base-100 shadow-2xl lg:card-side">
                <figure className="relative h-48 w-full overflow-hidden bg-base-200 sm:h-56 md:h-64 lg:h-80 lg:max-h-80 lg:w-1/2">
                  {featuredUpdate.image ? (
                    <img
                      alt={featuredUpdate.title}
                      className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                      src={
                        featuredUpdate.image?.startsWith("http")
                          ? featuredUpdate.image
                          : `${import.meta.env.VITE_API_STORAGE_URL}${featuredUpdate.image}`
                      }
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-base-300 to-base-200">
                      <span className="text-base-content/50">
                        No image available
                      </span>
                    </div>
                  )}
                </figure>
                <div className="card-body lg:w-1/2">
                  <div className="mb-4 flex items-center gap-4">
                    <span className="badge badge-primary">
                      {featuredUpdate.type}
                    </span>
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                      <Calendar size={16} />
                      <span>
                        {new Date(featuredUpdate.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <h3 className="card-title mb-4 text-2xl">
                    {featuredUpdate.title}
                  </h3>
                  <p className="mb-6 text-lg text-base-content/80">
                    {featuredUpdate.description}
                  </p>

                  <div className="flex items-center justify-between">
                    {featuredUpdate.subtitle && (
                      <div className="flex items-center gap-2">
                        <User size={16} />
                        <span className="text-sm">
                          {featuredUpdate.subtitle}
                        </span>
                      </div>
                    )}
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`/updates/${featuredUpdate.id}`)}
                    >
                      Read Full Story
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-base-content/70">
                No featured updates available at the moment.
              </div>
            )}
          </div>
        </section>

        {/* Filter Buttons */}
        <section className="bg-base-100 py-8">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-wrap justify-center gap-2">
              {filterOptions.map((filter) => (
                <button
                  className={`btn btn-sm rounded-full transition-all duration-200 ${
                    selectedFilter === filter
                      ? "btn-primary"
                      : "bg-base-200 text-base-content hover:bg-base-300"
                  }`}
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* News Grid */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12">
              <h2 className="mb-4 text-4xl font-bold">Recent Updates</h2>
              <p className="text-xl text-base-content/70">
                The latest news and developments from Hareeng
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : paginatedUpdates.length > 0 ? (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {Array.isArray(paginatedUpdates) && paginatedUpdates.map((update) => (
                    <div
                      className="card overflow-hidden bg-base-200 shadow-lg transition-shadow hover:shadow-xl"
                      key={update.id}
                    >
                      {update.image && (
                        <figure className="relative h-48 w-full overflow-hidden bg-base-300">
                          <img
                            alt={update.title}
                            className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
                            loading="lazy"
                            src={
                              update.image.startsWith("http")
                                ? update.image
                                : `${import.meta.env.VITE_API_STORAGE_URL}${update.image}`
                            }
                          />
                        </figure>
                      )}
                      <div className="card-body">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="badge badge-sm">{update.type}</span>
                          <div className="flex items-center gap-1 text-xs text-base-content/70">
                            <Clock size={12} />
                            <span>
                              {new Date(update.date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <h3 className="card-title mb-2 line-clamp-2 text-lg">
                          {update.title}
                        </h3>
                        <p className="mb-4 line-clamp-2 text-sm text-base-content/70">
                          {update.description.length > 144
                            ? update.description.substring(0, 144) + "..."
                            : update.description}
                        </p>

                        <div className="card-actions items-center justify-between">
                          {update.subtitle && (
                            <div className="line-clamp-1 flex items-center gap-1 text-xs">
                              <User size={12} />
                              <span>{update.subtitle}</span>
                            </div>
                          )}
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate(`/updates/${update.id}`)}
                          >
                            Read More
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="mt-12 text-center">
                    <button
                      className="btn btn-outline btn-lg"
                      onClick={loadMore}
                    >
                      Load More Updates
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-base-content/70">
                {selectedFilter === "All"
                  ? "No updates available at the moment."
                  : `No ${selectedFilter} updates available at the moment.`}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="bg-base-100 py-16" id="newsletter-section">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="mb-4 text-4xl font-bold">Never Miss an Update</h2>
            <p className="mb-8 text-xl opacity-90">
              Subscribe to our newsletter for the latest news, product updates,
              and insights
            </p>

            <form
              className="mx-auto max-w-md"
              onSubmit={handleNewsletterSubscribe}
            >
              <div className="space-y-3">
                <input
                  className="input input-bordered w-full text-base-content"
                  onChange={(e) => setNewsletterName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  type="text"
                  value={newsletterName}
                />
                <input
                  className="input input-bordered w-full text-base-content"
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  type="email"
                  value={newsletterEmail}
                />
                <button
                  className="btn btn-primary btn-block"
                  disabled={newsletterLoading}
                  type="submit"
                >
                  {newsletterLoading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>

              {newsletterMessage && (
                <div
                  className={`alert mt-4 ${
                    newsletterMessage.type === "success"
                      ? "alert-success"
                      : "alert-error"
                  }`}
                >
                  <span>{newsletterMessage.text}</span>
                </div>
              )}

              <p className="mt-2 text-sm opacity-75">
                No spam, unsubscribe anytime
              </p>
            </form>
          </div>
        </section>

        {/* Press & Media */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <h2 className="mb-4 text-4xl font-bold">Press & Media</h2>
              <p className="mb-8 text-xl text-base-content/70">
                Resources for journalists and media professionals
              </p>

              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={downloadPressKitPDF}
                >
                  <Download size={20} />
                  Download Press Kit
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
